import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ExternalLink, Loader2, Search } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { SectionCard } from '../../../components/design-system/admin';
import { CopyField } from '../../../components/design-system/common';
import {
    AGENT_TOOL_NAMES,
    AgentCommerceSettings,
    AgentProductCard,
    AgentToolName,
    TryToolResponse,
    fetchPublicText,
    useAiChannelsApi,
} from '../../../common/hooks/use-ai-channels-api.hook';
import { readApiError } from '../../../common/utils/api-error';

/**
 * Putting the catalogue inside ChatGPT, Claude & co.: the switch, how to add
 * the MCP server in each assistant, the agent-readable feed and llms.txt, and
 * a console that runs search_products exactly as an assistant would.
 */
export function AssistantsTab() {
    const { t } = useTranslation();
    const api = useAiChannelsApi();
    const [settings, setSettings] = useState<AgentCommerceSettings | null>(null);
    const [draft, setDraft] = useState({ publicName: '', description: '', tools: [] as AgentToolName[] });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.getSettings()
            .then((s) => {
                setSettings(s);
                setDraft({ publicName: s.publicName, description: s.description, tools: s.tools });
            })
            .catch(() => toast.error(t('AI_CHANNELS.LOAD_FAILED')));
    }, [api, t]);

    const save = async (patch: Parameters<typeof api.updateSettings>[0]) => {
        setSaving(true);
        try {
            const next = await api.updateSettings(patch);
            setSettings(next);
            toast.success(t('AI_CHANNELS.SAVED'));
        } catch (err) {
            const body = await readApiError(err);
            toast.error(body.message ? String(body.message) : t('AI_CHANNELS.SAVE_FAILED'));
        } finally {
            setSaving(false);
        }
    };

    if (!settings) return <Skeleton className="h-64 rounded-2xl" />;

    const dirty = draft.publicName !== settings.publicName || draft.description !== settings.description || draft.tools.join() !== settings.tools.join();

    return (
        <div className="flex flex-col gap-6" data-testid="ai-assistants">
            {/* The switch */}
            <SectionCard
                title={t('AI_CHANNELS.ASSISTANTS.ENABLE_TITLE')}
                description={t('AI_CHANNELS.ASSISTANTS.ENABLE_DESC')}
                action={
                    <label className="flex items-center gap-2 text-sm">
                        <Switch checked={settings.enabled} disabled={saving} onCheckedChange={(enabled) => save({ enabled })} data-testid="ai-assistants-enabled" />
                        {t(settings.enabled ? 'AI_CHANNELS.ASSISTANTS.ON' : 'AI_CHANNELS.ASSISTANTS.OFF')}
                    </label>
                }
            >
                <div className="flex flex-col gap-5">
                    <p className="text-xs text-muted-foreground">
                        {t('AI_CHANNELS.ASSISTANTS.CATALOGUE', {
                            products: settings.catalogue.products,
                            priced: settings.catalogue.priced,
                            stores: settings.catalogue.stores,
                        })}
                    </p>
                    <div className="grid gap-4 md:grid-cols-2">
                        <label className="flex flex-col gap-1.5">
                            <span className="text-xs font-medium text-muted-foreground">{t('AI_CHANNELS.ASSISTANTS.PUBLIC_NAME')}</span>
                            <Input value={draft.publicName} placeholder={settings.defaults.publicName} onChange={(e) => setDraft({ ...draft, publicName: e.target.value })} maxLength={120} />
                        </label>
                        <div className="flex flex-col gap-1.5">
                            <span className="text-xs font-medium text-muted-foreground">{t('AI_CHANNELS.ASSISTANTS.TOOLS')}</span>
                            <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                                {AGENT_TOOL_NAMES.map((tool) => (
                                    <label key={tool} className="flex items-center gap-2 text-sm">
                                        <Checkbox
                                            checked={draft.tools.includes(tool)}
                                            onCheckedChange={(on) => setDraft({ ...draft, tools: on ? AGENT_TOOL_NAMES.filter((x) => x === tool || draft.tools.includes(x)) : draft.tools.filter((x) => x !== tool) })}
                                        />
                                        {t(`AI_CHANNELS.ASSISTANTS.TOOL.${tool.toUpperCase()}`)}
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                    <label className="flex flex-col gap-1.5">
                        <span className="text-xs font-medium text-muted-foreground">{t('AI_CHANNELS.ASSISTANTS.DESCRIPTION')}</span>
                        <Textarea rows={3} value={draft.description} placeholder={settings.defaults.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} maxLength={1000} />
                        <span className="text-xs text-muted-foreground">{t('AI_CHANNELS.ASSISTANTS.DESCRIPTION_HINT')}</span>
                    </label>
                    <div>
                        <Button onClick={() => save(draft)} disabled={!dirty || saving}>
                            {saving && <Loader2 className="animate-spin" />}
                            {t('AI_CHANNELS.SAVE')}
                        </Button>
                    </div>
                </div>
            </SectionCard>

            {/* Connect */}
            <SectionCard title={t('AI_CHANNELS.ASSISTANTS.CONNECT_TITLE')} description={t('AI_CHANNELS.ASSISTANTS.CONNECT_DESC')}>
                <div className="flex flex-col gap-5">
                    <CopyField label={t('AI_CHANNELS.ASSISTANTS.MCP_URL')} value={settings.urls.mcp} testId="ai-assistants-mcp-url" />
                    {!settings.enabled && <p className="text-xs text-muted-foreground">{t('AI_CHANNELS.ASSISTANTS.OFF_HINT')}</p>}
                    <div className="grid gap-4 md:grid-cols-2">
                        {(['CHATGPT', 'CLAUDE'] as const).map((who) => (
                            <div key={who} className="rounded-xl border p-4">
                                <p className="text-sm font-medium">{t(`AI_CHANNELS.ASSISTANTS.${who}.TITLE`)}</p>
                                <ol className="mt-2 flex list-decimal flex-col gap-1.5 ps-5 text-sm text-muted-foreground">
                                    {[1, 2, 3, 4].map((n) => (
                                        <li key={n}>{t(`AI_CHANNELS.ASSISTANTS.${who}.STEP_${n}`, { url: settings.urls.mcp, name: settings.publicName || settings.defaults.publicName })}</li>
                                    ))}
                                </ol>
                                <p className="mt-3 text-xs text-muted-foreground">{t(`AI_CHANNELS.ASSISTANTS.${who}.NOTE`)}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </SectionCard>

            {/* Agent-readable catalogue */}
            <SectionCard title={t('AI_CHANNELS.ASSISTANTS.FEED_TITLE')} description={t('AI_CHANNELS.ASSISTANTS.FEED_DESC')}>
                <div className="grid gap-5 md:grid-cols-2">
                    <PublicPreview label={t('AI_CHANNELS.ASSISTANTS.LLMS')} url={settings.urls.llms} enabled={settings.enabled} testId="ai-assistants-llms" />
                    <PublicPreview label={t('AI_CHANNELS.ASSISTANTS.FEED')} url={settings.urls.feed} enabled={settings.enabled} json testId="ai-assistants-feed" />
                </div>
            </SectionCard>

            <TestConsole />
        </div>
    );
}

/** A public URL, copyable, with its first lines — fetched as anyone would, no credentials. */
function PublicPreview({ label, url, enabled, json, testId }: { label: string; url: string; enabled: boolean; json?: boolean; testId?: string }) {
    const { t } = useTranslation();
    const [text, setText] = useState<string | null>(null);

    useEffect(() => {
        if (!enabled) return;
        fetchPublicText(url, 60_000)
            .then((raw) => {
                if (!json) return setText(raw.split('\n').slice(0, 22).join('\n'));
                try {
                    const list = JSON.parse(raw);
                    const first = list?.itemListElement?.[0]?.item;
                    setText(JSON.stringify({ '@type': list['@type'], numberOfItems: list.numberOfItems, itemListElement: first ? [{ item: first }] : [] }, null, 2).split('\n').slice(0, 30).join('\n'));
                } catch {
                    setText(raw.slice(0, 1500));
                }
            })
            .catch(() => setText(null));
    }, [url, enabled, json]);

    return (
        <div className="flex min-w-0 flex-col gap-2" data-testid={testId}>
            <CopyField label={label} value={url} />
            {enabled ? (
                <>
                    <pre className="max-h-72 overflow-auto rounded-lg border bg-muted/40 p-3 font-mono text-[11px] leading-relaxed whitespace-pre-wrap break-words">{text ?? '…'}</pre>
                    <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                        <ExternalLink className="size-3" aria-hidden />
                        {t('AI_CHANNELS.ASSISTANTS.OPEN')}
                    </a>
                </>
            ) : (
                <p className="text-xs text-muted-foreground">{t('AI_CHANNELS.ASSISTANTS.OFF_HINT')}</p>
            )}
        </div>
    );
}

/** search_products, run as an assistant would, with the cards it returns and the JSON-RPC exchange. */
function TestConsole() {
    const { t } = useTranslation();
    const api = useAiChannelsApi();
    const [query, setQuery] = useState('');
    const [maxPrice, setMaxPrice] = useState('');
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState<TryToolResponse | null>(null);
    const [raw, setRaw] = useState(false);

    const run = async (e?: React.FormEvent) => {
        e?.preventDefault();
        setBusy(true);
        try {
            const args: Record<string, unknown> = {};
            if (query.trim()) args.query = query.trim();
            if (maxPrice.trim() && Number(maxPrice) > 0) args.max_price = Number(maxPrice);
            setResult(await api.tryTool('search_products', args));
        } catch (err) {
            const body = await readApiError(err);
            toast.error(body.message ? String(body.message) : t('AI_CHANNELS.ASSISTANTS.TRY_FAILED'));
        } finally {
            setBusy(false);
        }
    };

    const structured = result?.response?.result?.structuredContent;
    const products: AgentProductCard[] = structured?.products ?? [];
    const error = result?.response?.error?.message || (result?.response?.result?.isError ? result.response.result.content?.[0]?.text : null);

    return (
        <SectionCard title={t('AI_CHANNELS.ASSISTANTS.TRY_TITLE')} description={t('AI_CHANNELS.ASSISTANTS.TRY_DESC')} testId="ai-assistants-console">
            <form onSubmit={run} className="flex flex-col gap-3 sm:flex-row">
                <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('AI_CHANNELS.ASSISTANTS.TRY_PLACEHOLDER') as string} className="flex-1" data-testid="ai-console-query" />
                <Input value={maxPrice} onChange={(e) => setMaxPrice(e.target.value.replace(/[^\d.]/g, ''))} placeholder={t('AI_CHANNELS.ASSISTANTS.TRY_MAX_PRICE') as string} className="sm:w-40" inputMode="decimal" />
                <Button type="submit" disabled={busy} data-testid="ai-console-run">
                    {busy ? <Loader2 className="animate-spin" /> : <Search />}
                    {t('AI_CHANNELS.ASSISTANTS.TRY_RUN')}
                </Button>
            </form>

            {result && (
                <div className="mt-5 flex flex-col gap-4">
                    {error ? (
                        <p className="text-sm text-destructive">{error}</p>
                    ) : (
                        <>
                            <p className="text-sm text-muted-foreground">
                                {t('AI_CHANNELS.ASSISTANTS.TRY_FOUND', { count: products.length, total: structured?.totalMatches ?? products.length })}
                                {structured?.filters?.length ? ` · ${structured.filters.join(' · ')}` : ''}
                            </p>
                            {structured?.note && <p className="text-xs text-muted-foreground">{structured.note}</p>}
                            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="ai-console-cards">
                                {products.map((p) => <ProductCard key={p.id} product={p} />)}
                            </div>
                        </>
                    )}
                    <div>
                        <Button variant="outline" size="sm" onClick={() => setRaw((r) => !r)}>
                            {t(raw ? 'AI_CHANNELS.ASSISTANTS.HIDE_RAW' : 'AI_CHANNELS.ASSISTANTS.SHOW_RAW')}
                        </Button>
                        {raw && (
                            <pre className="mt-3 max-h-80 overflow-auto rounded-lg border bg-muted/40 p-3 font-mono text-[11px] leading-relaxed whitespace-pre-wrap break-all">
                                {`→ ${JSON.stringify(result.request)}\n\n← ${JSON.stringify(result.response, null, 2)}`}
                            </pre>
                        )}
                    </div>
                </div>
            )}
        </SectionCard>
    );
}

/** One card, as an assistant shows it: picture, name, price (or none published), size, the two links. */
function ProductCard({ product }: { product: AgentProductCard }) {
    const { t } = useTranslation();
    return (
        <div className="flex min-w-0 flex-col overflow-hidden rounded-xl border bg-card">
            {product.image ? (
                <img src={product.image} alt="" loading="lazy" className="aspect-square w-full object-cover" />
            ) : (
                <div className="aspect-square w-full bg-muted" />
            )}
            <div className="flex flex-1 flex-col gap-1 p-3">
                <p className="line-clamp-2 text-sm font-medium">{product.name}</p>
                <p className="text-sm tabular-nums">
                    {product.price ? product.price.label : <span className="text-xs text-muted-foreground">{t('AI_CHANNELS.ASSISTANTS.NO_PRICE')}</span>}
                </p>
                <p className="text-xs text-muted-foreground">{[product.size, product.colour].filter(Boolean).join(' · ')}</p>
                {product.matched?.length ? (
                    <span className="flex flex-wrap gap-1 pt-1">
                        {product.matched.map((m) => <Badge key={m} variant="secondary" className="font-normal">{m}</Badge>)}
                    </span>
                ) : null}
                <div className="mt-auto flex flex-wrap gap-x-3 gap-y-1 pt-2 text-xs">
                    <a href={product.seeInRoomUrl} target="_blank" rel="noreferrer" className="text-foreground underline-offset-2 hover:underline">{t('AI_CHANNELS.ASSISTANTS.SEE_IN_ROOM')}</a>
                    {product.productPageUrl && (
                        <a href={product.productPageUrl} target="_blank" rel="noreferrer" className="text-muted-foreground underline-offset-2 hover:underline">{t('AI_CHANNELS.ASSISTANTS.PRODUCT_PAGE')}</a>
                    )}
                </div>
            </div>
        </div>
    );
}
